'use client';

import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import type { SearchResponse } from 'meilisearch';
import type { ScrapedProduct, NlfOutput } from '@/lib/types';

import {
  shouldTriggerNLF,
  isSimplePriceQuery,
  shouldUseAiFirstFlow,
  findRelevantCategories,
  groupIntoPriceBands,
  priceBandLabel,
} from './searchHelpers';
import {
  MAX_STANDARD_FACETS,
  categoriesToHide,
} from './searchConstants';
import { useClientConfig } from '@/lib/clients/ClientContext';
import { buildCustomFilterGroups, priceFiltersEnabled } from '@/lib/clients/helpers';
import { useExternalPricing, PRICE_PRIORITY_WINDOW } from '@/lib/pricing/useExternalPricing';
import { parseBandKey } from '@/lib/pricing/priceBands';
import { logSettledSearch } from '@/lib/analytics/logSearch';
import { trackWidgetOpen } from '@/lib/analytics/track';

import SearchModal from './SearchModal';
import { useDismissOnOutsideTap } from './useDismissOnOutsideTap';
import { nextVisibilityAction } from '@/lib/hostVisibility';
import SearchHeader from './SearchHeader';
import SearchBar from './SearchBar';
// AiHintBanner removed — NLF disabled for now
import AiSuggestions from './AI/AiSuggestions';
import DefaultState from './DefaultState';
import FiltersSidebar from './Sidebar/FiltersSidebar';
import ProductSection from './ProductGrid/ProductSection';
import AiAnalyzingState from './AI/AiAnalyzingState';
import FiltersSidebarSkeleton from './Sidebar/FiltersSidebarSkeleton';
import NoResultsState from './NoResultsState';
import dynamic from 'next/dynamic';
import { ChatWidget } from '../chat';
const AgentChatWidget = dynamic(() => import('../agent-chat/AgentChatWidget'), { ssr: false });
import { SparkleIcon } from './icons';

const chatBackend = process.env.NEXT_PUBLIC_CHAT_BACKEND || 'agent';
import type { FilterGroup, ActiveFilterChip } from './types';
import { useViewMode } from '@/lib/viewMode';

type ProductSearchResponse = SearchResponse<ScrapedProduct>;

export default function SearchWidget() {
  const clientConfig = useClientConfig();
  // Recent searches live in the browser's localStorage (per device, never shared
  // between users). Namespaced by clientId so multiple clients sharing an origin
  // (e.g. our /demo/* pages) don't bleed each other's history.
  const recentSearchesKey = `recentSearches:${clientConfig.clientId}`;
  // API URL helper — includes ?client= param for multi-tenant routing.
  // Prefixed with apiOrigin when the widget runs on a cross-origin host
  // (e.g. aca.beta4u.gr): empty origin → relative URL → same-origin (Next.js
  // demo pages, our own host pages). Set by the widget bootstrap in main.tsx.
  const apiUrl = (path: string) =>
    `${clientConfig.apiOrigin ?? ''}${path}?client=${clientConfig.clientId}`;
  // ─── State ───────────────────────────────────────────────────────
  const [mode, setMode] = useState<'search' | 'chat'>('search');
  const [query, setQuery] = useState('');
  // Always-current query, for handlers (closeModal) that would otherwise close
  // over a stale value. Assigned every render — cheap and safe.
  const queryRef = useRef('');
  queryRef.current = query;
  const [results, setResults] = useState<ProductSearchResponse | null>(null);
  // searchId minted by /api/search for the current `results` set. Held in a
  // parallel state (updated in the SAME handler that calls setResults with the
  // fetched hits) so a later interaction reads the id belonging to the
  // displayed set, not a racing/stale one. Analytics data-capture only for now.
  const [resultsSearchId, setResultsSearchId] = useState<string | null>(null);
  // Settled-search logging. As-you-type fetches SHOW results but send settled:false
  // (so /api/search does NOT log every keystroke); the final query is logged once it
  // settles (idle 800ms) or the page unloads on a product click. pendingSearchRef
  // stashes the shown search; settledLoggedRef guards against double-logging one id.
  const pendingSearchRef = useRef<{ searchId: string | null; query: string; normalizedQuery: string | null; resultsCount: number; filtersUsed: string[] } | null>(null);
  const settledLoggedRef = useRef<string | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<NlfOutput | null>(null);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [isLoadingAi, setIsLoadingAi] = useState(false);
  const [isAiFirstFlow, setIsAiFirstFlow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Open the modal on mount when the client config requests it (e.g. acalight,
  // where the widget is dynamically shown inside the host's "search" overlay
  // so the user has already expressed intent to search by the time we mount).
  //
  // BUT only pre-open when the widget is actually VISIBLE. In embed mode the host
  // keeps the mount container (#ai-search-container) display:none until its own
  // trigger reveals it. Pre-opening while hidden makes SearchModal lock the HOST
  // page's <body> scroll on load (overflow:hidden) before the user sees anything —
  // the page then can't scroll until the modal is opened+closed. So when hidden at
  // mount we start CLOSED and let the IntersectionObserver below open the modal on
  // the host's hidden→visible reveal (the moment locking is actually correct).
  // Standalone/demo (no embed container) opens on mount as before.
  const [isModalOpen, setIsModalOpen] = useState(() => {
    if (!clientConfig.features?.autoOpen) return false;
    if (typeof document === 'undefined') return true; // SSR (demo) — open as before
    const container = document.getElementById('ai-search-container');
    if (!container) return true; // standalone/demo — no embed container
    return container.getClientRects().length > 0; // false when display:none
  });

  // NOTE: the host-container visibility observer lives further down, just below
  // `closeModal` — it calls that handler, so it has to be declared after it.

  // Word-split fallback groups (e.g. "gaming" and "πληκτρολόγιο" searched separately)
  const [wordSplitGroups, setWordSplitGroups] = useState<Array<{ word: string; droppedWord: string; hits: ScrapedProduct[]; facetDistribution: any }> | null>(null);

  // ONE grid/list choice shared by the home screen, the results and the chat,
  // starting on the client's default and remembered on the device.
  // Bumped after a scan that matched nothing → the search bar selects its text.
  const [scanSelectTick, setScanSelectTick] = useState(0);
  const [viewMode, setViewMode] = useViewMode(clientConfig.clientId, clientConfig.ui.defaultViewMode);
  // Narrow-screen (<lg) filters slide-over. On lg+ the persistent sidebar is
  // shown instead, so this only matters on tablet/mobile and only on the
  // results screen (the entry screen has no filters).
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  // Touch screens: a tap outside the open slide-over only closes it (see the hook).
  const filterDrawerRef = useRef<HTMLDivElement>(null);
  const closeFilterDrawer = useCallback(() => setFilterDrawerOpen(false), []);
  useDismissOnOutsideTap(filterDrawerRef, closeFilterDrawer, filterDrawerOpen);
  const [detectedFilterText, setDetectedFilterText] = useState<string>('');
  const isAiFrozenRef = useRef(false);
  const [selectedAiSuggestion, setSelectedAiSuggestion] = useState<string | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [popularProducts, setPopularProducts] = useState<ScrapedProduct[]>([]);
  const [showScrollBorder, setShowScrollBorder] = useState(false);
  const showScrollBorderRef = useRef(false);
  const [aiChipsHidden, setAiChipsHidden] = useState(false);
  const aiChipsHiddenRef = useRef(false);
  const scrollContainerElRef = useRef<HTMLDivElement | null>(null);
  const aiChipsDivRef = useRef<HTMLDivElement>(null);
  const [sidebarOffsetTop, setSidebarOffsetTop] = useState(0);
  const sidebarOffsetTopRef = useRef(0);

  const [appliedFilters, setAppliedFilters] = useState<{
    categories: string[];
    brands: string[];
    colors: string[];
    materials: string[];
    subcategories: string[];
    /** Selected price-band range keys ("0-20", "150+") — MULTI-select like
     *  every other checkbox group (founder call 2026-07-21; was single-select).
     *  Bands union (OR); a typed query range intersects the union. */
    priceRanges: string[];
    // Generic facet selections, keyed by `facetFields[i].field`. Every facet
    // that is not one of the legacy hardcoded slots above (color/material/
    // subcategory) lands here — e.g. ip_rating, light_type, light_color,
    // warehouses_available, etc. Empty arrays are pruned when reading.
    custom: Record<string, string[]>;
  }>({ categories: [], brands: [], colors: [], materials: [], subcategories: [], priceRanges: [], custom: {} });

  // Server-filtered results (when sidebar filters are active)
  const [serverFilteredHits, setServerFilteredHits] = useState<ScrapedProduct[] | null>(null);
  // searchId minted by /api/search for the current `serverFilteredHits` set —
  // paired the same way as `resultsSearchId`. When sidebar filters are active
  // the displayed set is `serverFilteredHits`, so its id supersedes the
  // unfiltered `resultsSearchId`.
  const [filteredSearchId, setFilteredSearchId] = useState<string | null>(null);
  const [isLoadingFiltered, setIsLoadingFiltered] = useState(false);
  // facetDistribution from the FILTERED search — Meilisearch's counts for the
  // current selection, computed over the whole match set (not our fetch window).
  // Consumed by `serverFacetDistribution` below; null whenever no filter is active.
  const [filteredFacetDistribution, setFilteredFacetDistribution] =
    useState<Record<string, Record<string, number>> | null>(null);
  // The search term that actually produced the current results.
  // For normal search: same as query. For AI flow: the AI-refined searchTerm.
  // Used by the filter effect so sidebar filters query with the right term.
  const effectiveSearchTermRef = useRef<string>('');

  const searchControllerRef = useRef<AbortController | null>(null);
  const aiControllerRef = useRef<AbortController | null>(null);
  const filterControllerRef = useRef<AbortController | null>(null);
  const scrollResizeObserver = useRef<ResizeObserver | null>(null);
  const [sidebarMaxH, setSidebarMaxH] = useState<number>(0);
  const scrollContainerRef = useCallback((node: HTMLDivElement | null) => {
    scrollContainerElRef.current = node;
    if (scrollResizeObserver.current) {
      scrollResizeObserver.current.disconnect();
      scrollResizeObserver.current = null;
    }
    if (node) {
      setSidebarMaxH(node.clientHeight);
      const ro = new ResizeObserver(([entry]) => {
        setSidebarMaxH(entry.contentRect.height);
      });
      ro.observe(node);
      scrollResizeObserver.current = ro;
    }
  }, []);

  // ─── Derived / Memoized ──────────────────────────────────────────

  // Display products: server-filtered when filters active, otherwise original hits
  const displayProducts = serverFilteredHits ?? results?.hits ?? [];
  // searchId for the currently-displayed set — when sidebar filters are active
  // the shown hits are `serverFilteredHits` (its own search), so its id
  // supersedes the unfiltered one. Threaded to ProductCard so a click/add-to-cart
  // joins to the search that produced the card.
  const displayedSearchId = serverFilteredHits ? filteredSearchId : resultsSearchId;

  // For clients with `features.externalPricing` (e.g. acalight, syfak), the
  // indexed RRP doesn't match the customer's actual price, so the widget
  // fetches per-customer prices for DISPLAY. Other clients short-circuit
  // (hook is a no-op).
  const externalPricingActive = !!(
    clientConfig.features?.externalPricing && clientConfig.externalPricing
  );

  // Whether price filters (sidebar bands + typed ranges) are offered at all.
  // False for every per-customer-pricing client — filtering on the indexed RRP
  // would answer a different question than the customer asked. See
  // priceFiltersEnabled() for the full rationale. B2C is unaffected.
  const priceFiltersOn = priceFiltersEnabled(clientConfig);

  // Fetch per-customer prices for the display set.
  //
  // …and for the ENTRY SCREEN's pinned popular products too (fixed 2026-07-27).
  // They render through the same ProductSection/ProductCard, but were never in
  // this id list, so they fell through to the indexed RRP — the FIRST thing a
  // B2B customer sees was a confident, wrong (list) price, plus no status tags
  // and no offer button, since those ride the same pricing response. It also
  // reads exactly like "my login isn't working", which cost real diagnosis time.
  // `displayProducts` is empty until a search runs, so this simply prices
  // whichever set is actually on screen — no extra request during search.
  const pricedProducts = displayProducts.length > 0 ? displayProducts : popularProducts;
  // …and only for products the shopper can actually SEE (2026-07-28). The grid
  // reveals 100 at a time on scroll and reports how many are showing; every
  // price is a live request to the client's own server, so pricing a 1,000-hit
  // result set up front asked them about ~900 products nobody ever scrolled to.
  // The hook extends its map as this grows, re-asking for nothing.
  //
  // The window is keyed to the displayed set and falls back to one batch the
  // moment that set changes. This has to resolve during RENDER: the grid resets
  // its own count in an effect, which lands a paint LATER, so a shopper who had
  // scrolled to 500 would briefly have the next search's first 500 products
  // priced — a request we would then abort, i.e. exactly the load on the
  // client's server this change exists to remove.
  const pricedSetKey = `${displayedSearchId ?? 'home'}:${pricedProducts.length}:${pricedProducts[0]?.id ?? ''}`;
  const pricedSetKeyRef = useRef(pricedSetKey);
  pricedSetKeyRef.current = pricedSetKey;
  const [revealed, setRevealed] = useState({ key: pricedSetKey, count: PRICE_PRIORITY_WINDOW });
  const revealedCount = revealed.key === pricedSetKey ? revealed.count : PRICE_PRIORITY_WINDOW;
  const handleVisibleCountChange = useCallback((count: number) => {
    setRevealed({ key: pricedSetKeyRef.current, count });
  }, []);
  const customerPriceIds = useMemo(
    () => (externalPricingActive ? pricedProducts.slice(0, revealedCount).map((p) => p.id) : []),
    [externalPricingActive, pricedProducts, revealedCount],
  );
  const { prices: customerPrices, loading: customerPricesLoading } =
    useExternalPricing(customerPriceIds, clientConfig);

  // Nothing narrows the displayed set in the browser any more. Until
  // 2026-07-28 an active B2B price filter was applied here, against the
  // resolved customer prices — that filter no longer exists, so what the
  // server returned is what renders. Kept as a named alias so the JSX below
  // (and any future post-processing) has one obvious place to hook into.
  //
  // Gone with it: a `customerPricesReady` guard that held the filter back until
  // the prices belonged to the CURRENT result set (without it, the first
  // price-filtered query of a session filtered new products against the
  // previous, empty map — the founder's "flash of no results on first load").
  // Nothing filters on prices now, and per-card display has its own pending
  // handling in ProductSection, so the guard has nothing left to protect.
  const finalDisplayProducts = displayProducts;

  // Facet counts come straight from whichever search describes what's on screen — the
  // widget does NO count arithmetic. /api/search returns counts that are already
  // correct for both cases: a field the user is NOT filtering on is counted with every
  // filter applied (= exactly what clicking it returns, since filters AND across
  // fields), and each field they ARE filtering on carries DISJUNCTIVE counts — its own
  // filter removed, everyone else's applied — so its number equals its click outcome
  // and its other values stay pickable (filters OR within a field). The server also
  // counts with the term that actually matched (Greeklish rescue included).
  //
  // A client-side "split rule" briefly lived here (33dac15), giving a touched field its
  // UNFILTERED counts. That is only right when nothing else is filtered: with
  // categories active, ticking a brand jumped its count 40 → 232 (the raw catalogue
  // number) while the grid correctly showed 40 — founder-found. Only the server sees
  // every filter AND every contributing search term, so only it can produce these
  // numbers. Do not reintroduce count math here.
  const serverFacetDistribution = filteredFacetDistribution ?? results?.facetDistribution;

  // Effective facet distribution — what the sidebar should display. Always the
  // server's numbers, straight through.
  //
  // A window-based recount lived here until 2026-07-20 — it rebuilt every
  // count from the ≤maxResultsToFetch fetch window, a relevance-skewed sample
  // (measured on acalight "led": IP44's true 491 rendered as 1; 31 of 38
  // colours vanished). Counting from the window is never honest — do not
  // reintroduce count math here; the server is the only side that can see the
  // whole match set. With B2B price filtering gone (2026-07-28) there is no
  // longer any filter the server can't count, so this is simply a pass-through.
  const effectiveFacetDistribution = serverFacetDistribution;

  // STABLE identity for the merged categories UI. Some names exist in BOTH taxonomy
  // fields ("Πιατέλες" is a category — 74 products for "piata" — AND a subcategory —
  // 168). The merged group shows one row per NAME, category winning. If the dedupe is
  // done against the currently *visible* category rows, the identity flaps: tick a
  // subcategory filter, the category twin drops to 0 and hides, and the subcategory
  // twin surfaces under the same label with a different number — the founder watched
  // "Πιατέλες 74" silently become "Πιατέλες 168". So the suppression set is the
  // UNFILTERED response's category names — fixed for the life of the query — and a name
  // that is a category for this query is NEVER shown as its subcategory twin. When its
  // category count hits 0 under the current filters it simply disappears, exactly like
  // every other exhausted option, and returns on clear.
  const categoryNamesForQuery = useMemo(
    () => new Set(Object.keys(results?.facetDistribution?.category ?? {})),
    [results?.facetDistribution?.category]
  );

  const relevantCategories = useMemo(
    (): { name: string; count: number; key?: string }[] => {
      const cats = findRelevantCategories(query, effectiveFacetDistribution?.category)
        .filter(({ name }) => !categoriesToHide.has(name));
      const catNames = new Set(cats.map(c => c.name));
      const subs = findRelevantCategories(query, effectiveFacetDistribution?.subcategory)
        .filter(({ name }) => !catNames.has(name) && !categoryNamesForQuery.has(name))
        .map(({ name, count }) => ({ name, count, key: `sub:${name}` }));
      return [...cats, ...subs].sort((a, b) => b.count - a.count);
    },
    [query, effectiveFacetDistribution?.category, effectiveFacetDistribution?.subcategory, categoryNamesForQuery]
  );

  const standardCategoryFacets = useMemo(() => {
    const items: { name: string; count: number; key?: string }[] = [];
    const allCats = effectiveFacetDistribution?.category;
    if (allCats) {
      const relevant = new Set(relevantCategories.filter(c => !c.key).map(c => c.name));
      items.push(
        ...Object.entries(allCats)
          .filter(([name]) => !relevant.has(name) && !categoriesToHide.has(name))
          .sort(([, a], [, b]) => b - a)
          .slice(0, MAX_STANDARD_FACETS)
          .map(([name, count]) => ({ name, count }))
      );
    }
    const allSubs = effectiveFacetDistribution?.subcategory;
    if (allSubs) {
      const relevantSubs = new Set(relevantCategories.filter(c => c.key?.startsWith('sub:')).map(c => c.name));
      const allCatNames = new Set([
        ...relevantCategories.filter(c => !c.key).map(c => c.name),
        ...items.map(i => i.name),
      ]);
      items.push(
        ...Object.entries(allSubs)
          // categoryNamesForQuery: stable name-collision suppression — see its memo above.
          .filter(([name]) => !relevantSubs.has(name) && !allCatNames.has(name) && !categoryNamesForQuery.has(name))
          .sort(([, a], [, b]) => b - a)
          .slice(0, MAX_STANDARD_FACETS)
          .map(([name, count]) => ({ name, count, key: `sub:${name}` }))
      );
    }
    items.sort((a, b) => b.count - a.count);
    return items;
  }, [effectiveFacetDistribution?.category, effectiveFacetDistribution?.subcategory, relevantCategories, categoryNamesForQuery]);

  // priceBands reads the server distribution directly, which needs no
  // special-casing — `price` is just another field: with bands selected the server
  // returns its DISJUNCTIVE counts (all bands within the other filters, so they stay
  // pickable — the multi-band OR string still registers `price` as user-filtered via
  // filterFieldOf's paren-skip), and with none selected the bands refine to the
  // current filters. Under an ACTIVE price filter on external-pricing clients this
  // distribution is id-scoped and unusable for the band list — filterGroups builds
  // that state's list from the BASE distribution instead (see below).
  const priceBands = useMemo(
    () => groupIntoPriceBands(serverFacetDistribution?.price),
    [serverFacetDistribution?.price]
  );

  // Build filter groups for FiltersSidebar.
  // Counts come from `effectiveFacetDistribution` → `serverFacetDistribution`: a field
  // the user is filtering on keeps its unfiltered counts (so its other options stay
  // pickable — filters OR within a field), every other field refines to the current
  // selection and drops its dead values. See lib/facetCounts.ts + the route's
  // disjunctive-counts block.
  const filterGroups = useMemo<FilterGroup[]>(() => {
    const groups: FilterGroup[] = [];
    if (relevantCategories.length > 0) {
      groups.push({ title: clientConfig.ui.strings.suggestedCategories, maxVisible: 5, items: relevantCategories });
    }
    if (standardCategoryFacets.length > 0) {
      groups.push({ title: clientConfig.ui.strings.otherCategories, maxVisible: 5, items: standardCategoryFacets });
    }
    const brandFacets = effectiveFacetDistribution?.brand;
    if (brandFacets && Object.keys(brandFacets).length > 0) {
      groups.push({
        title: clientConfig.ui.strings.brandLabel,
        maxVisible: 5,
        items: Object.entries(brandFacets)
          .sort(([, a], [, b]) => b - a)
          .map(([name, count]) => ({ name, count })),
      });
    }
    // Dynamic facet fields from client config (color, material, etc.)
    const customGroups = buildCustomFilterGroups(effectiveFacetDistribution, clientConfig);
    groups.push(...customGroups);
    // Price-range bands — B2C only. Per-customer-pricing clients get NO price
    // group at all: the only price we hold is the indexed RRP, which is not
    // what that customer pays, so every band count and every band click would
    // be answering a different question than the one asked (see
    // priceFiltersEnabled). Multi-select bands with server-computed counts,
    // selected band highlighted (parousiasi/kaizershop UX) — unchanged.
    if (priceFiltersOn && priceBands.length > 0) {
      groups.push({
        title: clientConfig.ui.strings.priceRange,
        items: priceBands.map(({ label, count, range }) => ({
          name: label,
          count,
          key: range,
        })),
      });
    }
    return groups;
  }, [relevantCategories, standardCategoryFacets, effectiveFacetDistribution, priceBands, clientConfig, priceFiltersOn]);

  // All selected filter items (for FiltersSidebar highlight)
  const allSelectedFilterItems = useMemo(
    () => [
      ...appliedFilters.categories,
      ...appliedFilters.brands,
      ...appliedFilters.colors,
      ...appliedFilters.materials,
      ...appliedFilters.subcategories.map(s => `sub:${s}`),
      ...Object.values(appliedFilters.custom).flat(),
      ...appliedFilters.priceRanges,
    ],
    [appliedFilters.categories, appliedFilters.brands, appliedFilters.colors, appliedFilters.materials, appliedFilters.subcategories, appliedFilters.custom, appliedFilters.priceRanges]
  );

  // Active filters as removable chips for the sidebar's "Ενεργά φίλτρα" block.
  // Keys encode slot + value for `removeFilterChip` below.
  const activeFilterChips = useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];
    for (const c of appliedFilters.categories) chips.push({ key: `cat:${c}`, label: c });
    for (const s of appliedFilters.subcategories) chips.push({ key: `sub:${s}`, label: s });
    for (const b of appliedFilters.brands) chips.push({ key: `brand:${b}`, label: b });
    for (const c of appliedFilters.colors) chips.push({ key: `color:${c}`, label: c });
    for (const m of appliedFilters.materials) chips.push({ key: `material:${m}`, label: m });
    for (const [field, values] of Object.entries(appliedFilters.custom)) {
      for (const v of values) chips.push({ key: `custom:${field}:${v}`, label: v });
    }
    // One chip PER selected band, all clients (multi-select). Labels come from
    // the static band table — NOT the current facet distribution, which under
    // an id-scoped search may no longer contain a selected band. On external-
    // pricing clients the TYPED query range additionally shows as its own chip
    // via FiltersSidebar's `activePriceFilter` prop.
    for (const r of appliedFilters.priceRanges) {
      chips.push({ key: `priceband:${r}`, label: priceBandLabel(r) ?? r });
    }
    return chips;
  }, [appliedFilters]);

  // AI suggestion chips for AiSuggestions component
  const aiSuggestionChips = useMemo(() => {
    if (!aiSuggestions) return [];
    return [...(aiSuggestions.suggestedCategories || []), ...(aiSuggestions.suggestedKeywords || [])];
  }, [aiSuggestions]);

  // ─── Effects ─────────────────────────────────────────────────────
  React.useEffect(() => {
    const stored = localStorage.getItem(recentSearchesKey);
    if (stored) {
      try { setRecentSearches(JSON.parse(stored)); } catch {}
    } else {
      setRecentSearches([]);
    }
  }, [recentSearchesKey]);

  // Count this mount — and note that it is NOT logged as a search. Mounts used to
  // land in `search_events` as NULL-query rows (the fetch below forgot
  // `settled:false`), which was 57% of the whole analytics table; that is fixed,
  // but it was also the signal that diagnosed the June-2026 embed loop, so the
  // alarm is rebuilt here as an aggregate counter that stores nothing per-mount.
  //
  // `first` comes from sessionStorage, which is what separates the two numbers:
  // `opens` counts mounts, `sessions` counts people. A looping embed drives them
  // apart (opens climbing, sessions flat) — that divergence IS the detector, so
  // don't "simplify" this by dropping the flag.
  React.useEffect(() => {
    let first = false;
    try {
      const key = `widgetOpened:${clientConfig.clientId}`;
      first = sessionStorage.getItem(key) === null;
      if (first) sessionStorage.setItem(key, '1');
    } catch {
      // Blocked storage (Safari private mode, embedded third-party contexts):
      // count the open, not the session. Undercounting people is the safe
      // direction — never invent them.
    }
    trackWidgetOpen(clientConfig.apiOrigin, clientConfig.clientId, first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const fetchPopularProducts = async () => {
      try {
        // Pinned "popular products": when the client config lists specific SKUs
        // (ui.popularProductCodes), show exactly those, in that order. Otherwise
        // fall back to the top empty-search results that have images.
        const pinnedCodes = clientConfig.ui.popularProductCodes;
        const body = {
          query: '',
          filters: pinnedCodes && pinnedCodes.length > 0
            ? [`model IN [${pinnedCodes.map((c) => `"${c}"`).join(', ')}]`]
            : [],
          // This is the widget MOUNTING, not a shopper searching — so it must not
          // be recorded as a search. /api/search logs anything that doesn't opt out
          // (`if (settled !== false)`), and this call omitted the flag entirely, so
          // every mount wrote a search_events row with a NULL query: 6,033 of
          // syfak's 9,837 rows on go-live day (~1.6 junk rows per real search).
          // Nothing merchant-facing was ever wrong — the funnel rollup, all four
          // report queries and the retention gate each filter `query IS NOT NULL`
          // already — so the cost was purely write volume plus raw counts that read
          // ~2.6x high and could never answer "how many people opened the search".
          // Deliberately NOT fixed by threading visitorId/sessionId in here: that
          // would attribute a non-search to a real shopper and move the inflation
          // into the funnel's search stage, where it WOULD be visible and wrong.
          settled: false,
        };
        const res = await fetch(apiUrl('/api/search'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          const data = await res.json();
          const hits: ScrapedProduct[] = data.results?.hits || [];
          if (pinnedCodes && pinnedCodes.length > 0) {
            // Keep ONLY the configured products, in the configured order
            // (Meilisearch IN-filters don't preserve order, and the codes are
            // hand-picked so we don't drop no-image ones here).
            const byModel = new Map(hits.map((h) => [String((h as { model?: string }).model ?? ''), h]));
            const ordered = pinnedCodes
              .map((c) => byModel.get(String(c)))
              .filter((h): h is ScrapedProduct => Boolean(h));
            setPopularProducts(ordered);
          } else {
            // Default state should look polished — surface only products that
            // have actual images. Fetch the top set, filter, take first 8.
            const withImages = hits.filter((h) => typeof h.image_url === 'string' && h.image_url.length > 0);
            setPopularProducts(withImages.slice(0, 8));
          }
        }
      } catch (e) {
        console.error('Failed to fetch popular products:', e);
      }
    };
    fetchPopularProducts();
  }, []);

  // Set initial sidebar offset when sidebar becomes visible (AI chip clicked)
  // or reset when AI chips disappear
  useEffect(() => {
    if (selectedAiSuggestion && aiChipsDivRef.current && scrollContainerElRef.current) {
      const scrollTop = scrollContainerElRef.current.scrollTop;
      const offset = Math.max(0, aiChipsDivRef.current.offsetHeight - scrollTop);
      sidebarOffsetTopRef.current = offset;
      setSidebarOffsetTop(offset);
    }
    if (!aiSuggestionChips.length) {
      sidebarOffsetTopRef.current = 0;
      setSidebarOffsetTop(0);
    }
  }, [selectedAiSuggestion, aiSuggestionChips.length]);

  // ─── Server-side filter effect ─────────────────────────────────
  // When sidebar filters change, re-query Meilisearch with filter params.
  // Uses effectiveSearchTermRef (the AI-refined term when in AI flow,
  // or the raw query for normal search) so filters match the actual results.
  useEffect(() => {
    // Price bands go straight into Meilisearch, and only exist on clients that
    // offer price filtering at all (B2C). On per-customer-pricing clients
    // `priceRanges` can never be populated — there is no band UI to populate it
    // — so the guard is belt-and-braces rather than load-bearing.
    const hasCustomFilters = Object.values(appliedFilters.custom).some(arr => arr.length > 0);
    const hasNonPriceFilters = appliedFilters.categories.length > 0
      || appliedFilters.brands.length > 0
      || appliedFilters.colors.length > 0
      || appliedFilters.materials.length > 0
      || appliedFilters.subcategories.length > 0
      || hasCustomFilters;
    const priceTriggersServerRefetch =
      priceFiltersOn && appliedFilters.priceRanges.length > 0;
    const hasFilters = hasNonPriceFilters || priceTriggersServerRefetch;

    if (!hasFilters || !query.trim()) {
      // Abort any in-flight filtered fetch FIRST: without this, a stale
      // response (e.g. a scoped search for a mid-typing query like "led εως 5")
      // can land AFTER this clear and repaint its — possibly empty — result
      // set over the fresh unfiltered view (observed as a stuck "no results"
      // on the acalight beta, 2026-07-20).
      if (filterControllerRef.current) {
        filterControllerRef.current.abort();
        filterControllerRef.current = null;
      }
      setServerFilteredHits(null);
      setFilteredSearchId(null);
      // No selection ⇒ nothing to refine against. Clearing this is what makes
      // the sidebar snap back to the full unfiltered counts, so every "clear"
      // path (uncheck the last box, Clear all, close, new query) restores the
      // original bar through this one branch.
      setFilteredFacetDistribution(null);
      return;
    }

    // Use the effective search term (AI-refined or raw query)
    const searchTerm = effectiveSearchTermRef.current || query;

    // Build Meilisearch filter strings
    const meilisearchFilters: string[] = [];
    if (appliedFilters.categories.length > 0) {
      meilisearchFilters.push(
        `category IN [${appliedFilters.categories.map(c => `"${c}"`).join(', ')}]`
      );
    }
    if (appliedFilters.brands.length > 0) {
      meilisearchFilters.push(
        `brand IN [${appliedFilters.brands.map(b => `"${b}"`).join(', ')}]`
      );
    }
    if (appliedFilters.colors.length > 0) {
      meilisearchFilters.push(
        `color IN [${appliedFilters.colors.map(c => `"${c}"`).join(', ')}]`
      );
    }
    if (appliedFilters.materials.length > 0) {
      meilisearchFilters.push(
        `material IN [${appliedFilters.materials.map(m => `"${m}"`).join(', ')}]`
      );
    }
    if (appliedFilters.subcategories.length > 0) {
      meilisearchFilters.push(
        `subcategory IN [${appliedFilters.subcategories.map(s => `"${s}"`).join(', ')}]`
      );
    }
    // Price bands (B2C only — per-customer-pricing clients have no band UI).
    if (appliedFilters.priceRanges.length > 0 && priceFiltersOn) {
      // Multi-select bands = OR of parenthesized compounds in ONE filter
      // string (array elements AND together on the route, so the union must
      // stay inside a single element). parseBandKey handles the open-ended
      // "150+" key — naive '-'-splitting once built `price < NaN`, which
      // Meilisearch matches against NOTHING (found 2026-07-21). The route's
      // filterFieldOf skips leading '(' so price still registers as a
      // user-filtered field and keeps its disjunctive band counts.
      const parts = appliedFilters.priceRanges
        .map(parseBandKey)
        .filter((b) => b.min !== null || b.max !== null)
        .map((b) =>
          b.max === null
            ? `(price >= ${b.min})`
            : b.min === null
              ? `(price < ${b.max})`
              : `(price >= ${b.min} AND price < ${b.max})`,
        );
      if (parts.length > 0) meilisearchFilters.push(parts.join(' OR '));
    }

    // Generic custom-facet filters (everything beyond the hardcoded slots
    // above — e.g. ip_rating, light_type, light_color, warehouses_available
    // for acalight). Keyed by the facetFields[i].field name, which is also
    // the Meilisearch document attribute name.
    for (const [field, values] of Object.entries(appliedFilters.custom)) {
      if (values.length > 0) {
        meilisearchFilters.push(
          `${field} IN [${values.map(v => `"${v.replace(/"/g, '\\"')}"`).join(', ')}]`
        );
      }
    }

    // Abort previous filter request
    if (filterControllerRef.current) filterControllerRef.current.abort();
    filterControllerRef.current = new AbortController();
    const signal = filterControllerRef.current.signal;

    setIsLoadingFiltered(true);

    console.log('🔎 Filter query using effective term:', { searchTerm, raw: query, filters: meilisearchFilters });

    fetch(apiUrl('/api/search'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: searchTerm,
        filters: meilisearchFilters,
        visitorId: clientConfig.visitorId,
        sessionId: clientConfig.sessionId,
        externalCustomerId: clientConfig.externalCustomerId,
        warehouseCode: clientConfig.warehouseCode,
        // Every refetch here is a DELIBERATE filter act (ticked checkbox,
        // clicked price band), so it logs as a settled filter_refine. Until
        // 2026-07-28 a typed price could also trigger this effect per keystroke
        // via the B2B price scope, which is why the flag was computed rather
        // than hardcoded; that path is gone with B2B price filtering.
        settled: true,
        // Same search, narrowed — not a new one. Logged as 'filter_refine' so ticking
        // four filters on "led" records one search, not five. See the route's log call.
        isFilterRefine: true,
      }),
      signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Filter search failed');
        const data = await response.json();
        const filteredHits = data.results?.hits || [];
        setServerFilteredHits(filteredHits);
        // Pair the response's searchId with the exact hits we just stored.
        setFilteredSearchId(data.searchId ?? null);
        // Meilisearch's counts for the current selection. Computed over the FULL
        // match set (facetDistribution ignores `limit`), so these are the real
        // numbers, not a recount of the hits above. Feeds the split rule.
        setFilteredFacetDistribution(data.results?.facetDistribution ?? null);
        console.log('🔎 Server-side filter applied:', {
          effectiveQuery: searchTerm,
          filters: meilisearchFilters,
          resultCount: filteredHits.length,
          totalHits: data.results?.estimatedTotalHits,
        });
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error('Filter fetch error:', err);
      })
      .finally(() => {
        // Only the fetch that is STILL the current one may clear the loading
        // flag: an aborted predecessor's finally runs a microtask AFTER its
        // replacement set loading=true, and clearing it then lets the
        // NoResults branch paint over an in-flight fetch (found by the
        // 2026-07-21 adversarial review).
        if (filterControllerRef.current?.signal === signal) setIsLoadingFiltered(false);
      });
  }, [appliedFilters, query, priceFiltersOn]);

  // ─── Close helper ────────────────────────────────────────────────
  const closeModal = useCallback(() => {
    // Kill anything in flight BEFORE resetting state — otherwise a late
    // response repaints stale results (and a stale extractedPriceFilter) over
    // the closed modal and settle-logs the abandoned query. Mirrors the
    // empty-query branch of handleInputChange.
    searchControllerRef.current?.abort();
    filterControllerRef.current?.abort();
    debouncedFetchResults.cancel();
    debouncedFetchAiSuggestions.cancel();
    debouncedAiFirstFlow.cancel();
    // The shopper SAW these results — log the settled search now instead of
    // letting the timer race the state reset (previous behavior logged it
    // post-close anyway; this keeps that semantic deterministically).
    debouncedLogSettled.flush();
    setIsModalOpen(false);
    // Every open starts on the search. Closing unmounts the chat (the modal
    // renders its children only while open), so the conversation is already
    // gone — keeping `mode` meant the next tap on the host's search icon
    // landed in an EMPTY Αριάδνη chat instead of the search, which read as
    // "the chat opens by itself" (syfak, 2026-10-27).
    setMode('search');
    setFilterDrawerOpen(false);
    setQuery('');
    setResults(null);
    setResultsSearchId(null);
    setWordSplitGroups(null);
    setAiSuggestions(null);
    setServerFilteredHits(null);
    setFilteredSearchId(null);
    setFilteredFacetDistribution(null);
    setAppliedFilters({ categories: [], brands: [], colors: [], materials: [], subcategories: [], priceRanges: [], custom: {} });

    // Notify the host so it can run its own cleanup (collapse its overlay,
    // clear inline styles, etc.). Hosts using Option B (autoOpen + own
    // wrapper) define a global `closeSearch()` function on `window`. This
    // is the standard close contract — see docs/WIDGET_EMBED_GUIDE.md.
    if (typeof window !== 'undefined') {
      const hostCloseSearch = (window as unknown as {
        closeSearch?: () => void;
      }).closeSearch;
      if (typeof hostCloseSearch === 'function') {
        try {
          hostCloseSearch();
        } catch (err) {
          console.warn('[search] host closeSearch() threw:', err);
        }
      }
    }
  }, []);

  // Always-current `isModalOpen` for the observer below, which must not
  // re-subscribe every time the modal opens or closes.
  const isModalOpenRef = useRef(isModalOpen);
  useEffect(() => { isModalOpenRef.current = isModalOpen; }, [isModalOpen]);

  // ─── Host-container visibility ───────────────────────────────────
  // The widget mounts inside a container the HOST shows and hides (syfak's
  // `#my-search-wrapper`, acalight's mobile menu). Two transitions matter, and
  // for a long time we only handled one.
  //
  // hidden → visible: the host opened its overlay, so open the modal.
  //
  // visible → hidden: the host closed us WITHOUT going through `closeModal` —
  // its own close control, an outside-click handler, a menu collapse. Nothing
  // else would ever call dialog.close(), so the native modal stayed in the
  // browser's TOP LAYER: the search vanished with the container while every
  // click on the host page was still blocked and the body scroll-lock stayed
  // on. The page read as completely frozen, which is exactly what syfak's team
  // reported on 2026-09-10 («παγώνει όλη η σελίδα»). Reproduced on their
  // staging embed — page inert, search invisible — and closing here makes that
  // state unreachable regardless of WHICH host path hid us.
  //
  // ⚠️ The test is `getClientRects()`, NOT `entry.isIntersecting`.
  // IntersectionObserver is used because it fires on display:none toggles, but
  // it also fires on ordinary scrolling — and this container sits in the host's
  // header, so scrolling it out of view would otherwise be read as "hidden" and
  // would close the modal under the user. getClientRects() is empty only when
  // the element genuinely is not rendered.
  React.useEffect(() => {
    if (!clientConfig.features?.autoOpen) return;
    if (typeof window === 'undefined') return;
    const host = document.getElementById('ai-search-container');
    if (!host) return;

    // Track it ourselves — the observer's first callback reports the initial
    // state, which must not count as a transition. The decision itself lives in
    // `nextVisibilityAction` (pure, unit-tested): IntersectionObserver only
    // delivers callbacks in a genuinely rendered tab, so this branch cannot be
    // covered by an automated browser pass.
    let wasRendered: boolean | null = null;
    const sync = () => {
      const isRendered = host.getClientRects().length > 0;
      const action = nextVisibilityAction(wasRendered, isRendered, isModalOpenRef.current);
      if (action === 'open') setIsModalOpen(true);
      if (action === 'close') closeModal();
      wasRendered = isRendered;
    };
    const observer = new IntersectionObserver(sync, { threshold: 0 });
    observer.observe(host);

    // 🔴 AND A POLL, because the observer ALONE is not dependable.
    //
    // IntersectionObserver only reports what the browser actually renders: it
    // delivers nothing in a background tab, and a host's zero-height wrapper
    // (syfak's `#my-search-wrapper` has no CSS at all) is a zero-area target
    // where its behaviour is underspecified. If the visible→hidden callback
    // never arrives, the modal stays open in the top layer with the host page
    // inert. So do not rely on the observer noticing — ASK, cheaply, while we
    // are open. One getClientRects() on one element, only while the modal is
    // open, is nothing; a blocked storefront is not. The observer stays because
    // it reacts instantly where it does work; this is the floor under it.
    //
    // (Added 2026-09-14 against syfak's "frozen" search. That report turned
    // out to be a different bug — our backdrop click leaking into their
    // `onclick="openSearch()"`, fixed in SearchModal — so this poll is a
    // safety net, not the cure.)
    const poll = window.setInterval(() => {
      if (!isModalOpenRef.current) return;
      if (host.getClientRects().length === 0) {
        console.warn(
          '[search] The host hid our container while the search was open. ' +
            'Closing so the page cannot be left blocked.',
        );
        closeModal();
      }
    }, 400);

    return () => {
      observer.disconnect();
      window.clearInterval(poll);
    };
  }, [clientConfig.features?.autoOpen, closeModal]);

  // ─── Settled-search logging ──────────────────────────────────────
  // Log the CURRENT shown search as "settled" (idempotent per searchId). Fired on
  // idle-settle (800ms) and on page unload (a product click navigates away); never
  // for a query the shopper cleared/abandoned (pendingSearchRef nulled). sendBeacon
  // inside logSettledSearch survives the unload, so the click still joins to it.
  const flushSettledSearch = useCallback(() => {
    const p = pendingSearchRef.current;
    if (!p || !p.query || !p.searchId) return;
    if (settledLoggedRef.current === p.searchId) return; // already logged this search
    settledLoggedRef.current = p.searchId;
    logSettledSearch(clientConfig.apiOrigin, clientConfig.clientId, {
      searchId: p.searchId,
      query: p.query,
      normalizedQuery: p.normalizedQuery,
      resultsCount: p.resultsCount,
      filtersUsed: p.filtersUsed.length > 0 ? p.filtersUsed : undefined,
      visitorId: clientConfig.visitorId,
      sessionId: clientConfig.sessionId,
      externalCustomerId: clientConfig.externalCustomerId,
    });
  }, [clientConfig]);

  // 800ms of no typing = the query has settled → log it. Restarted on every fetch.
  const debouncedLogSettled = useDebouncedCallback(() => flushSettledSearch(), 800);

  // A product click navigates the host page away (modal closes); flush on unload so
  // the search is recorded and the click still joins to it.
  useEffect(() => {
    const onHide = () => flushSettledSearch();
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [flushSettledSearch]);

  // ─── Core fetch ──────────────────────────────────────────────────
  const fetchResults = useCallback(async (currentQuery: string, filters?: string[]) => {
    if (!currentQuery.trim()) {
      setResults(null);
      setResultsSearchId(null);
      setError(null);
      setIsLoadingResults(false);
      return;
    }
    if (searchControllerRef.current) searchControllerRef.current.abort();
    searchControllerRef.current = new AbortController();
    const signal = searchControllerRef.current.signal;

    effectiveSearchTermRef.current = currentQuery;
    setIsLoadingResults(true);
    setError(null);
    const startTime = Date.now();

    fetch(apiUrl('/api/search'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: currentQuery,
        filters: filters || undefined,
        visitorId: clientConfig.visitorId,
        sessionId: clientConfig.sessionId,
        externalCustomerId: clientConfig.externalCustomerId,
        warehouseCode: clientConfig.warehouseCode,
        settled: false, // as-you-type: SHOW results but don't log; the settle path logs the final query
      }),
      signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Search API error: ${response.statusText}`);
        const searchData = await response.json();
        // The query may have been cleared/changed while the body was in flight —
        // don't repaint stale results over the (now default) view.
        if (signal.aborted) return;
        console.log('📦 Search API response:', {
          query: currentQuery,
          totalHits: searchData.results?.estimatedTotalHits ?? searchData.results?.hits?.length,
          resultsCount: searchData.results?.hits?.length,
          firstResult: searchData.results?.hits?.[0]?.title,
        });
        setResults(searchData.results);
        // Pair the response's searchId with the exact hits we just stored.
        setResultsSearchId(searchData.searchId ?? null);
        setWordSplitGroups(searchData.wordSplitGroups || null);
        // Stash the shown search + (re)start the 800ms settle timer. /api/search did
        // NOT log this (settled:false); the settle/unload path logs the FINAL query.
        pendingSearchRef.current = {
          searchId: searchData.searchId ?? null,
          query: currentQuery,
          normalizedQuery: searchData.normalizedQuery ?? null,
          resultsCount: searchData.results?.hits?.length ?? 0,
          filtersUsed: filters ?? [],
        };
        // A barcode scan that found its product: empty the box but keep the
        // result, so the pharmacist can scan the next box without clearing it by
        // hand — otherwise the next scan is appended to this one (syfak,
        // 2026-09-28). Only when the box still holds exactly what we searched:
        // if they already started typing, their text wins. The empty box would
        // normally mean "home screen"; `results` being set keeps us on the
        // results screen (see isEntryScreen). A scan is complete the moment it
        // arrives, so it is logged now — the next scan's first keystroke would
        // otherwise cancel the 800ms settle timer and lose it.
        if (searchData.scannedBarcode && queryRef.current === currentQuery) {
          flushSettledSearch();
          setQuery('');
        } else if (searchData.unmatchedScan && queryRef.current === currentQuery) {
          // Found nothing: keep the code visible so the pharmacist can see what
          // was read, but select it so the next scan replaces it.
          flushSettledSearch();
          setScanSelectTick((n) => n + 1);
        } else {
          debouncedLogSettled();
        }
        console.log('✅ Search results loaded at', Date.now() - startTime, 'ms');
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error('Search error:', err);
        setError((err as Error).message || 'Failed to fetch search results');
      })
      .finally(() => setIsLoadingResults(false));
  }, []);

  const debouncedFetchResults = useDebouncedCallback(fetchResults, 200);

  const debouncedFetchAiSuggestions = useDebouncedCallback(async (currentQuery: string) => {
    if (!currentQuery.trim()) {
      setAiSuggestions(null);
      setIsLoadingAi(false);
      return;
    }
    if (!isSimplePriceQuery(currentQuery) && shouldTriggerNLF(currentQuery)) {
      if (aiControllerRef.current) {
        aiControllerRef.current.abort();
        console.log('🚫 Aborted previous AI request');
      }
      aiControllerRef.current = new AbortController();
      const signal = aiControllerRef.current.signal;
      if (!aiSuggestions) setIsLoadingAi(true);
      const startTime = Date.now();

      fetch(apiUrl('/api/search/nlf-agent'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: currentQuery }),
        signal,
      })
        .then(async (response) => {
          if (!response.ok) { console.warn('NLF agent API returned error status'); return null; }
          const data = await response.json();
          if (data.triggered && data.suggestions) {
            setAiSuggestions(data.suggestions);
            console.log('✅ NLF agent suggestions loaded at', Date.now() - startTime, 'ms');

            // If agent returned products, replace keyword search results
            if (data.products?.length > 0) {
              effectiveSearchTermRef.current = data.suggestions.searchTerm || currentQuery;
              const agentResults: ProductSearchResponse = {
                hits: data.products,
                estimatedTotalHits: data.totalFound || data.products.length,
                facetDistribution: data.facetDistribution || {},
                query: currentQuery,
                processingTimeMs: data.durationMs || 0,
              } as ProductSearchResponse;
              setResults(agentResults);
              // AI results come from the nlf-agent path, which doesn't thread a
              // searchId into resultsSearchId — null it so cards don't carry a
              // STALE search_id from the prior keyword search. (NLF is disabled
              // today, so this is future-proofing against a re-enable.)
              setResultsSearchId(null);
              setIsLoadingResults(false);
            }

            if (data.suggestions.filters?.length > 0) {
              const m = currentQuery.match(
                /(μέχρι|μεχρι|έως|εως|κάτω από|κατω απο|κατω|κάτω|πάνω από|πανω απο|πανω|πάνω|under|over|below|above)\s+(\d+(\.\d+)?)\s*(€|ευρω|ευρώ)?/i
              );
              if (m) setDetectedFilterText(m[0]);
            }
          }
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          console.warn('NLF agent error (non-critical):', err);
        })
        .finally(() => setIsLoadingAi(false));
    } else {
      console.log('AI not triggered for query:', currentQuery);
    }
  }, 1800);

  const debouncedAiFirstFlow = useDebouncedCallback(async (currentQuery: string) => {
    if (!currentQuery.trim()) {
      setResults(null);
      setAiSuggestions(null);
      setIsAiFirstFlow(false);
      setIsLoadingAi(false);
      return;
    }
    if (aiControllerRef.current) aiControllerRef.current.abort();
    aiControllerRef.current = new AbortController();
    const signal = aiControllerRef.current.signal;

    setIsAiFirstFlow(true);
    setIsLoadingAi(true);
    setResults(null);
    const startTime = Date.now();
    console.log('🧠 NLF-AGENT FLOW: Starting for query:', currentQuery);

    try {
      // One-shot NLF agent: LLM interprets → additive search → products + suggestions
      const response = await fetch(apiUrl('/api/search/nlf-agent'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: currentQuery }),
        signal,
      });
      if (!response.ok) throw new Error('NLF agent API returned error');
      const data = await response.json();
      console.log('✅ NLF agent response at', Date.now() - startTime, 'ms');

      if (data.triggered && data.products?.length > 0) {
        // Set AI suggestions for chips
        setAiSuggestions(data.suggestions);
        effectiveSearchTermRef.current = data.suggestions?.searchTerm || currentQuery;

        // Build a ProductSearchResponse-compatible object from agent results
        const agentResults: ProductSearchResponse = {
          hits: data.products,
          estimatedTotalHits: data.totalFound || data.products.length,
          facetDistribution: data.facetDistribution || {},
          query: currentQuery,
          processingTimeMs: data.durationMs || 0,
        } as ProductSearchResponse;

        setResults(agentResults);
        setResultsSearchId(null); // AI results carry no search_id — avoid a stale one (see note above)
        console.log('📦 NLF agent results:', data.products.length, 'products');

        // Detect price filter text for display
        if (data.suggestions?.filters?.length > 0) {
          const m = currentQuery.match(
            /(μέχρι|μεχρι|έως|εως|κάτω από|κατω απο|κατω|κάτω|πάνω από|πανω απο|πανω|πάνω|under|over|below|above)\s+(\d+(\.\d+)?)\s*(€|ευρω|ευρώ)?/i
          );
          if (m) setDetectedFilterText(m[0]);
        }
      } else {
        console.log('⚠️ NLF agent returned no results, falling back to keyword search');
        debouncedFetchResults(currentQuery);
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      console.error('❌ NLF agent flow error:', err);
      debouncedFetchResults(currentQuery);
    } finally {
      setIsAiFirstFlow(false);
      setIsLoadingAi(false);
      setIsLoadingResults(false);
    }
  }, 1000);

  // ─── Handlers ────────────────────────────────────────────────────
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => applyQuery(event.target.value);

  // Everything a new query does — shared by typing and by voice input, so a
  // spoken query is searched exactly as if it had been typed.
  const applyQuery = (newQuery: string) => {
    setQuery(newQuery);

    // A keystroke means the query hasn't settled yet — hold off the settle-log until
    // the NEXT fetch resolves and re-arms it, so the 800ms idle is measured from the
    // last KEYSTROKE, not the last fetch resolution. Without this, a partial that
    // already resolved (e.g. "iph") could settle-log while the user is still typing
    // the full word — re-introducing the fragment pollution this feature prevents.
    debouncedLogSettled.cancel();

    // Always reset state from previous query
    if (selectedAiSuggestion !== null) setSelectedAiSuggestion(null);
    isAiFrozenRef.current = false;
    effectiveSearchTermRef.current = newQuery;
    setAppliedFilters({ categories: [], brands: [], colors: [], materials: [], subcategories: [], priceRanges: [], custom: {} });
    setServerFilteredHits(null);
    setFilteredFacetDistribution(null);
    setAiSuggestions(null);
    setDetectedFilterText('');
    setError(null);
    setResults(null);
    // Instant scroll reset (no animation) so new content starts from top
    if (scrollContainerElRef.current) scrollContainerElRef.current.scrollTop = 0;

    if (newQuery.trim()) {
      // Record the settled query as a recent search (debounced).
      debouncedAddRecent(newQuery);
      // Show product loading skeleton immediately so content swaps instantly
      // (like "clear" UX — no scroll animation, just fresh content)
      setIsLoadingResults(true);

      if (shouldUseAiFirstFlow(newQuery)) {
        // Don't set AI loading here — let the debounced function do it
        // after user stops typing (1000ms). Product skeleton shows in the meantime.
        debouncedFetchResults.cancel();
        debouncedFetchAiSuggestions.cancel();
        debouncedAiFirstFlow(newQuery);
      } else {
        setIsAiFirstFlow(false);
        setIsLoadingAi(false);
        debouncedAiFirstFlow.cancel();
        debouncedFetchResults(newQuery);
        debouncedFetchAiSuggestions(newQuery);
      }
    } else {
      setResults(null);
      setIsLoadingResults(false);
      setIsLoadingAi(false);
      setIsAiFirstFlow(false);
      // Cancelling the debounce only stops a not-yet-fired search; a fetch that is
      // ALREADY in flight would still resolve and repaint its results over the home
      // screen after the box was cleared. Abort it too, so clearing the query
      // reliably returns to the default (popular-products) state.
      searchControllerRef.current?.abort();
      debouncedFetchResults.cancel();
      debouncedFetchAiSuggestions.cancel();
      debouncedAiFirstFlow.cancel();
      // The shopper abandoned this query — don't let it settle-log (now or on unload).
      debouncedLogSettled.cancel();
      pendingSearchRef.current = null;
    }
  };

  const handleAiSuggestionClick = useCallback((value: string) => {
    setSelectedAiSuggestion(value);
    setAppliedFilters({ categories: [], brands: [], colors: [], materials: [], subcategories: [], priceRanges: [], custom: {} });
    setServerFilteredHits(null);
    setFilteredFacetDistribution(null);
    isAiFrozenRef.current = true;
    setQuery(value);
    debouncedFetchResults.cancel();
    fetchResults(value, aiSuggestions?.filters || []);
  }, [fetchResults, debouncedFetchResults, aiSuggestions?.filters]);

  const handleExampleClick = useCallback((value: string) => {
    isAiFrozenRef.current = false;
    setQuery(value);
    if (shouldUseAiFirstFlow(value)) {
      setIsAiFirstFlow(true);
      setIsLoadingAi(true);
      debouncedAiFirstFlow(value);
    } else {
      setIsLoadingResults(true);
      setIsLoadingAi(true);
      debouncedFetchResults(value);
      debouncedFetchAiSuggestions(value);
    }
  }, [debouncedFetchResults, debouncedFetchAiSuggestions, debouncedAiFirstFlow]);

  const addToRecentSearches = useCallback((searchTerm: string) => {
    if (!searchTerm.trim()) return;
    setRecentSearches(prev => {
      const filtered = prev.filter(s => s.toLowerCase() !== searchTerm.toLowerCase());
      const updated = [searchTerm, ...filtered].slice(0, 5);
      localStorage.setItem(recentSearchesKey, JSON.stringify(updated));
      return updated;
    });
  }, [recentSearchesKey]);

  // Record a TYPED query as recent once it settles (1s after the last
  // keystroke), so live-typed searches — not just chip clicks — show up under
  // "Αναζητήσατε πρόσφατα". Not cancelled when the query is cleared, so a quick
  // "search → go home" still records the term a moment later.
  const debouncedAddRecent = useDebouncedCallback((term: string) => {
    if (term.trim().length >= 2) addToRecentSearches(term.trim());
  }, 1000);

  const removeFromRecentSearches = useCallback((searchTerm: string) => {
    setRecentSearches(prev => {
      const updated = prev.filter(s => s !== searchTerm);
      localStorage.setItem(recentSearchesKey, JSON.stringify(updated));
      return updated;
    });
  }, [recentSearchesKey]);

  const handleQuickSearchClick = useCallback((label: string) => {
    // A top-search chip may override what it actually searches (ui.topSearchActions,
    // keyed by label) — e.g. the "Τ 4" chip searches "t4" to hit the t4 synonym.
    const action = clientConfig.ui.topSearchActions?.[label];
    const searchTerm = action?.query ?? label;
    setQuery(searchTerm);
    addToRecentSearches(searchTerm);
    setIsLoadingResults(true);
    debouncedFetchResults(searchTerm);
    if (shouldTriggerNLF(searchTerm)) {
      setIsLoadingAi(true);
      debouncedFetchAiSuggestions(searchTerm);
    }
  }, [clientConfig, addToRecentSearches, debouncedFetchResults, debouncedFetchAiSuggestions]);

  const handleFilterToggle = useCallback((
    filterType: 'categories' | 'brands' | 'colors' | 'materials' | 'subcategories' | 'priceRanges',
    value: string,
  ) => {
    // Every slot — price bands included since 2026-07-21 — is a plain
    // multi-select array toggle (the bands render as checkboxes; a checkbox
    // that behaves like a radio is a broken promise — founder feedback).
    setAppliedFilters(prev => {
      const cur = prev[filterType];
      return {
        ...prev,
        [filterType]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value],
      };
    });
  }, []);

  // Toggle a value on a generic (non-legacy) custom facet, keyed by field name.
  // Used for every facet beyond color/material/subcategory — e.g. ip_rating,
  // light_type, light_color, warehouses_available, etc. (94 facets total in
  // the acalight config).
  const handleCustomFilterToggle = useCallback((field: string, value: string) => {
    setAppliedFilters(prev => {
      const cur = prev.custom[field] ?? [];
      const next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value];
      return { ...prev, custom: { ...prev.custom, [field]: next } };
    });
  }, []);

  // Remove ONE active filter, addressed by its chip key (see activeFilterChips
  // for the key grammar). The custom-facet case splits off the field name and
  // rejoins the rest so facet VALUES containing ':' survive intact; field
  // names themselves are plain identifiers (ip_rating, light_type, …).
  const removeFilterChip = useCallback((chipKey: string) => {
    if (chipKey.startsWith('priceband:')) {
      handleFilterToggle('priceRanges', chipKey.slice('priceband:'.length));
      return;
    }
    const [kind, ...rest] = chipKey.split(':');
    if (kind === 'custom') {
      const [field, ...valueParts] = rest;
      handleCustomFilterToggle(field, valueParts.join(':'));
      return;
    }
    const value = rest.join(':');
    const slotMap = {
      cat: 'categories', brand: 'brands', color: 'colors',
      material: 'materials', sub: 'subcategories',
    } as const;
    const slot = slotMap[kind as keyof typeof slotMap];
    if (slot) handleFilterToggle(slot, value);
  }, [handleCustomFilterToggle, handleFilterToggle]);

  // Clear every active filter but KEEP the query and its results — the
  // sidebar's "Καθαρισμός όλων". Deliberately not handleClearAll (below),
  // which is the go-back-home reset that also wipes the search text. State
  // only: the server-side filter effect sees the emptied selection and
  // restores the unfiltered results + counts through its no-filters branch.
  const clearAllFilters = useCallback(() => {
    setAppliedFilters({ categories: [], brands: [], colors: [], materials: [], subcategories: [], priceRanges: [], custom: {} });
  }, []);

  // Adapter for FiltersSidebar: maps group title → filter type.
  // Items with key "sub:Name" are subcategories merged into the category group.
  // Legacy fields (color/material/subcategory) keep their typed slots for
  // backward compatibility; every other configured facet lands in `custom`
  // keyed by the field name (e.g. ip_rating, light_type, warehouses_available).
  const handleSidebarFilterToggle = useCallback((groupTitle: string, itemName: string) => {
    if (groupTitle === clientConfig.ui.strings.brandLabel) {
      handleFilterToggle('brands', itemName);
    } else if (groupTitle === clientConfig.ui.strings.priceRange) {
      handleFilterToggle('priceRanges', itemName);
    } else if (itemName.startsWith('sub:')) {
      handleFilterToggle('subcategories', itemName.slice(4));
    } else {
      const facetField = clientConfig.facetFields.find(f => f.label === groupTitle);
      if (facetField) {
        const legacyKeyMap: Record<string, 'colors' | 'materials' | 'subcategories'> = {
          color: 'colors', material: 'materials', subcategory: 'subcategories',
        };
        const legacyKey = legacyKeyMap[facetField.field];
        if (legacyKey) {
          handleFilterToggle(legacyKey, itemName);
        } else {
          handleCustomFilterToggle(facetField.field, itemName);
        }
      } else {
        handleFilterToggle('categories', itemName);
      }
    }
  }, [handleFilterToggle, handleCustomFilterToggle, clientConfig]);

  /** Opens the assistant. Defined ONCE and shared by every SearchBar below.
   *  It was previously inlined on the entry screen only, so the two
   *  active-search branches rendered a SearchBar with no launcher — typing a
   *  query made the assistant unreachable at every screen size, on every
   *  client. Keeping one definition is what stops that recurring. */
  const openChatLauncher = useCallback(
    () => setMode('chat'),
    [],
  );

  const handleClearAll = useCallback(() => {
    // Kill in-flight/debounced fetches FIRST — a late response would repaint
    // the cleared screen with stale results (same class as the closeModal fix).
    searchControllerRef.current?.abort();
    debouncedFetchResults.cancel();
    debouncedFetchAiSuggestions.cancel();
    debouncedAiFirstFlow.cancel();
    // "Go back home" — record what they just searched so it shows under recents.
    debouncedAddRecent.flush();
    // Clearing discards the current search from analytics too (cancel any pending settle).
    debouncedLogSettled.cancel();
    pendingSearchRef.current = null;
    if (queryRef.current.trim().length >= 2) addToRecentSearches(queryRef.current.trim());
    setAppliedFilters({ categories: [], brands: [], colors: [], materials: [], subcategories: [], priceRanges: [], custom: {} });
    setFilterDrawerOpen(false);
    setSelectedAiSuggestion(null);
    setServerFilteredHits(null);
    setFilteredFacetDistribution(null);
    setQuery('');
    setResults(null);
    setAiSuggestions(null);
    setDetectedFilterText('');
    isAiFrozenRef.current = false;
    effectiveSearchTermRef.current = '';
  }, [debouncedAddRecent, addToRecentSearches, debouncedLogSettled]);

  /** Props for EVERY SearchBar below (entry screen + both results branches).
   *  One object, spread at each site, so a new control can never again reach
   *  only one of the three bars — the way the chat launcher once did. */
  const searchBarProps = {
    query,
    onChange: handleInputChange,
    onClear: handleClearAll,
    isLoading: isLoadingResults,
    onOpenChat: clientConfig.features?.agentChat !== false ? openChatLauncher : undefined,
    onVoiceQuery: applyQuery,
    selectAllSignal: scanSelectTick,
  };

  const handleContentScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    const shouldShow = scrollTop > 10;
    if (shouldShow !== showScrollBorderRef.current) {
      showScrollBorderRef.current = shouldShow;
      setShowScrollBorder(shouldShow);
    }
    // AI chips are ~80px tall; consider them hidden once mostly scrolled past
    const chipsHidden = scrollTop > 60;
    if (chipsHidden !== aiChipsHiddenRef.current) {
      aiChipsHiddenRef.current = chipsHidden;
      setAiChipsHidden(chipsHidden);
    }
    // Compute how much of the AI chips area is still visible in the viewport
    const aiChipsEl = aiChipsDivRef.current;
    const aiOffset = aiChipsEl ? Math.max(0, aiChipsEl.offsetHeight - scrollTop) : 0;
    if (Math.abs(aiOffset - sidebarOffsetTopRef.current) > 2) {
      sidebarOffsetTopRef.current = aiOffset;
      setSidebarOffsetTop(aiOffset);
    }
  }, []);

  const handleAiScrollTop = useCallback(() => {
    scrollContainerElRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);


  // ─── Helpers: has active search? ─────────────────────────────────
  const hasActiveSearch = !!(results || isLoadingResults || isAiFirstFlow);
  const isEntryScreen = !hasActiveSearch && !query.trim();

  // ─── Render ──────────────────────────────────────────────────────
  return (
    <>
      {/* Embedded Search Bar (triggers modal). Hidden when the client config
          uses features.autoOpen — modal opens directly on mount in that case. */}
      {!clientConfig.features?.autoOpen && (
        <div className="sticky top-0 z-40 flex justify-center px-4 py-3">
          <div className="bg-white rounded-lg shadow-sm px-4 py-3 w-full max-w-2xl">
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-500 bg-transparent border-b-2 border-gray-300 hover:border-brand focus:outline-none focus:border-brand transition-all cursor-pointer"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="flex-1">{clientConfig.ui.strings.searchingProducts}</span>
            </button>
          </div>
        </div>
      )}

      {/* Search Modal */}
      <SearchModal isOpen={isModalOpen} onClose={closeModal}>
        {/* ── Chat Mode ────────────────────────────────────────── */}
        {mode === 'chat' && (
          chatBackend === 'agent'
            ? <AgentChatWidget onClose={closeModal} onSwitchToSearch={() => setMode('search')} viewMode={viewMode} onViewModeChange={setViewMode} />
            : <ChatWidget onClose={closeModal} onSwitchToSearch={() => setMode('search')} />
        )}

        {/* ── Entry Screen ─────────────────────────────────────── */}
        {mode === 'search' && isEntryScreen && (
          <div className="flex flex-col h-full items-start gap-6 bg-white">
            <div className="flex flex-col items-start gap-[18px] self-stretch">
              <SearchHeader onClose={closeModal} />
              <SearchBar {...searchBarProps} />
            </div>
            <DefaultState
              recentSearches={recentSearches}
              topSearches={clientConfig.ui.topSearches}
              popularProducts={popularProducts}
              onSearchClick={handleQuickSearchClick}
              onRemoveRecent={removeFromRecentSearches}
              externalPrices={customerPrices}
              externalPricesLoading={customerPricesLoading}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
          </div>
        )}

        {/* ── Active Search Screen ─────────────────────────────── */}
        {mode === 'search' && !isEntryScreen && (
          <div className={`flex flex-col h-full items-start bg-white ${isAiFirstFlow && !aiSuggestions ? '' : aiSuggestionChips.length > 0 ? 'gap-[18px]' : 'gap-6'}`}>
            {/* Header area — different structure depending on AI active or not */}
            {(aiSuggestionChips.length > 0 || isAiFirstFlow) ? (
              /* AI flow (loading or results): flat gap-[18px], consistent structure */
              <div className="flex flex-col items-start gap-[18px] self-stretch">
                <SearchHeader onClose={closeModal} />
                <SearchBar {...searchBarProps} />
                {isAiFirstFlow && !aiSuggestions ? (
                  <AiAnalyzingState />
                ) : null}
              </div>
            ) : (
              /* Normal search (no AI): header + search bar */
              <div className="flex flex-col items-start gap-[18px] self-stretch">
                <SearchHeader onClose={closeModal} />
                <SearchBar {...searchBarProps} />
              </div>
            )}

            {/* Scrollable content area */}
            <div
              ref={scrollContainerRef}
              className={`flex-1 self-stretch min-h-0 overflow-y-auto overscroll-contain custom-scrollbar smooth-scroll ${
                results && results.hits.length === 0 && !error && !isAiFirstFlow && !isLoadingFiltered && !isLoadingResults ? 'flex flex-col' : ''
              } ${
                (!isAiFirstFlow && !aiSuggestionChips.length) || showScrollBorder ? 'border-t border-[#D8D8D8]' : ''
              }`}
              onScroll={aiSuggestionChips.length > 0 ? handleContentScroll : undefined}
            >
              {/* AI suggestions — full width, scrolls away naturally */}
              {aiSuggestionChips.length > 0 && (
                <div ref={aiChipsDivRef} className="flex-shrink-0">
                  <AiSuggestions suggestions={aiSuggestionChips} onSuggestionClick={handleAiSuggestionClick} />
                </div>
              )}

              {/* No results — full width centered, replaces the two-column layout. */}
              {((results && results.hits.length === 0 && !wordSplitGroups) ||
                (serverFilteredHits !== null && serverFilteredHits.length === 0 && !wordSplitGroups))
                && !error && !isAiFirstFlow && !isLoadingFiltered && !isLoadingResults ? (
                <NoResultsState />
              ) : (
                /* Two-column layout — sidebar sticky, products flow */
                <div className="flex items-start self-stretch">
                  {/* Tablet/mobile filters slide-over (<lg). Overlays the LEFT of
                      the products, pinned just below the sticky products header
                      (top-[64px]); negative right-margin so it doesn't push the
                      grid, and products stay visible to the right (no dim). The
                      «Φίλτρα» button toggles it; the panel's own ✕ closes it, and
                      on a touch screen so does any tap outside it (which then
                      opens nothing beneath). Reuses FiltersSidebar. */}
                  {filterDrawerOpen && (
                    <div
                      ref={filterDrawerRef}
                      className="lg:hidden sticky top-[84px] self-start z-30 w-[280px] -mr-[280px] overflow-hidden shadow-[2px_2px_4px_0px_rgba(0,0,0,0.1)]"
                      style={{ height: sidebarMaxH ? `${sidebarMaxH - 84}px` : '70vh' }}
                    >
                      <div className="h-full animate-slideInLeft">
                        <FiltersSidebar
                          groups={filterGroups}
                          selectedItems={allSelectedFilterItems}
                          onFilterToggle={handleSidebarFilterToggle}
                          highlightQuery={query}
                          activeChips={activeFilterChips}
                          onRemoveChip={removeFilterChip}
                          onClearAllFilters={clearAllFilters}
                          onCollapse={closeFilterDrawer}
                        />
                      </div>
                    </div>
                  )}
                  {/* Filters sidebar — hidden on mobile, hidden during initial AI results until chip clicked */}
                  {!(aiSuggestionChips.length > 0 && selectedAiSuggestion === null) && (
                    <div
                      className="hidden lg:block flex-shrink-0 sticky top-0 self-start"
                      style={sidebarMaxH ? { height: `${sidebarMaxH - sidebarOffsetTop}px` } : undefined}
                    >
                      {(isAiFirstFlow || !results) ? (
                        <FiltersSidebarSkeleton />
                      ) : (
                        <div className="animate-fadeIn h-full">
                          <FiltersSidebar
                            groups={filterGroups}
                            selectedItems={allSelectedFilterItems}
                            onFilterToggle={handleSidebarFilterToggle}
                            highlightQuery={query}
                            activeChips={activeFilterChips}
                            onRemoveChip={removeFilterChip}
                            onClearAllFilters={clearAllFilters}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Products */}
                  <div className="flex flex-col items-start flex-1 min-w-0">
                    {error ? (
                      <div className="p-6 w-full">
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-red-700 text-[14px] font-gotham">{error}</p>
                        </div>
                      </div>
                    ) : (isAiFirstFlow || isLoadingFiltered || (isLoadingResults && !results)) ? (
                      <ProductSection
                        title={clientConfig.ui.strings.productsTitle}
                        products={[]}
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        isLoading={true}
                      />
                    ) : finalDisplayProducts.length > 0 ? (
                      <ProductSection
                        title={clientConfig.ui.strings.productsTitle}
                        // Numbers only. This was the one hardcoded ENGLISH string
                        // left in an otherwise fully Greek UI («Προϊόντα»,
                        // «Φίλτρα», «ΤΙΜΗ ΡΟΗΣ» all sit around it), and the two
                        // counts carry the whole meaning without either language.
                        // Deliberately NOT translated to Greek: this component is
                        // shared by every client, so a bare "20 / 269" stays
                        // correct if a non-Greek client is ever onboarded. If
                        // per-client wording is wanted later it belongs in
                        // ui.strings — which needs types + all 5 client configs +
                        // both serialize sites + clientConfigFromDb fallbacks.
                        subtitle={(() => {
                          const shown = finalDisplayProducts.length;
                          const total = results?.estimatedTotalHits ?? results?.hits?.length ?? 0;
                          return (
                            <>
                              {/* Phone: «X από Y». Measured against the real
                                  header budget (row width minus the 167px
                                  toolbar) with syfak's worst case — they have
                                  25,496 products, so the counts run to 5 digits:
                                    budget   320:130  375:185  390:200  430:240
                                    full     236px → fits ONLY a Pro Max
                                    «X από Y» 90px → fits every phone
                                  «Προβολή» and «αποτελέσματα» are the expensive
                                  words and carry the least meaning; «από» does
                                  the same job as a slash but reads as Greek.
                                  Shrinking the font was the alternative and was
                                  rejected — 10px still fails a 12 mini. */}
                              <span className="md:hidden">{shown} από {total}</span>
                              <span className="hidden md:inline">
                                Προβολή {shown} από {total} αποτελέσματα
                              </span>
                            </>
                          );
                        })()}
                        products={finalDisplayProducts}
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        isLoading={false}
                        showAiScrollTop={aiChipsHidden && aiSuggestionChips.length > 0}
                        onAiScrollTop={handleAiScrollTop}
                        highlightQuery={query}
                        externalPrices={customerPrices}
                        externalPricesLoading={customerPricesLoading}
                        onOpenFilters={() => setFilterDrawerOpen((o) => !o)}
                        filtersOpen={filterDrawerOpen}
                        activeFilterCount={allSelectedFilterItems.length}
                        searchId={displayedSearchId}
                        source="search"
                        onVisibleCountChange={handleVisibleCountChange}
                      />
                    ) : wordSplitGroups && wordSplitGroups.length > 0 ? (
                      <div className="flex flex-col items-center justify-center flex-1 p-10 animate-fadeIn w-full">
                        <p className="text-[16px] font-gotham font-medium text-text-primary leading-[28px] text-center">
                          {clientConfig.ui.strings.noResults} &quot;{query}&quot;
                        </p>
                        <p className="text-[14px] font-gotham font-normal text-text-secondary leading-[28px] mt-2 text-center">
                          Δοκιμάστε:
                        </p>
                        <div className="flex items-center justify-center gap-3 mt-3 flex-wrap">
                          {wordSplitGroups.map((group, idx) => (
                            <button
                              key={idx}
                              onClick={() => {
                                setQuery(group.word);
                                setWordSplitGroups(null);
                                fetchResults(group.word);
                              }}
                              className="px-4 py-2 rounded-lg bg-brand/10 text-brand font-gotham font-medium text-[14px] hover:bg-brand hover:text-brand-text transition-colors cursor-pointer"
                            >
                              {group.word}
                              <span className="ml-1.5 text-text-secondary text-[12px] font-normal">({group.hits.length})</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <ProductSection
                        title={clientConfig.ui.strings.productsTitle}
                        products={[]}
                        viewMode={viewMode}
                        onViewModeChange={setViewMode}
                        isLoading={true}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </SearchModal>
    </>
  );
}
